import { ImsObject } from 'app/shared/collections/ims_object';
import { HelpDetails, HelpRegistry } from 'app/shared/help_and_information/HelpRegistry';
import { TOUR_TYPE__IMS_ITEM_PANEL } from 'app/shared/help_and_information/tours/TourService';
import { DynamicStatSchemaCollection } from '../collections/dynamic_stat_schema_collection.service';
import { StatDefinition } from '../collections/StatDefinition';
import OneShotEventRegistry from '../events/one_shot_event_registry';
import { QuickstartGuideService } from '../quickstart_guide/quickstart_guide.service';

interface PanelDetails {
    stateKey: string;
    inUrl: boolean;
    bodyComponentName: string;
    configOverrides?: any;
}

export type ImsItemHelpDetails = Partial<HelpDetails>;

interface ExtendedHelpDetails extends HelpDetails {
    quickStartGuideSearchText: string;
}

export interface ImsItemTypeDetails {
    displayName: string; // The name of the item type
    name?: string;
    collection; // The ImsObjectCollection used to load the item type
    ItemClass; // The ImsObject subclass used for this IMS Item
    isStat?: boolean; // Whether or not this IMS Item has a timestamp and is a timeseries object (stat)
    statDefinition?: StatDefinition; // The optional stat definition used to add metadata about a stat
    panelDetails?: PanelDetails; // Used to specify the config for the panel associated with this IMSObject
    helpDetails?: ImsItemHelpDetails; // Used to specify the help details for this IMS Object
    hasApiSafeUpdate?: boolean; // Used to enable the safe update end point
    /**
     * optionally provide a function which turns the ims object into a POJO for filtering; include any properties
     * the user may want to fitler by.
     * @param imsObject
     */
    toFilterableItem?: (imsObject: ImsObject) => any;
}

export class ImsItemTypeRegistry {
    private _typeData: Map<string, ImsItemTypeDetails>;
    private _helpRegistry: HelpRegistry;
    private _imsItemTypeRegistryReady: OneShotEventRegistry;
    private _dynamicStatSchemaCollection: DynamicStatSchemaCollection;
    private _quickstartGuidePageFinder: QuickstartGuideService;

    constructor(helpRegistry: HelpRegistry, QuickstartGuideService: QuickstartGuideService) {
        this._typeData = new Map();
        this._helpRegistry = helpRegistry;
        this._imsItemTypeRegistryReady = new OneShotEventRegistry();
        this._quickstartGuidePageFinder = QuickstartGuideService;

        // For a bit of safety since it's very important that
        // setDynamicStatSchemaCollection is called shortly after
        // registry creation else the application won't get kick-started with
        // dynamic stats
        setTimeout(() => {
            if (!this._dynamicStatSchemaCollection) {
                throw new Error(`Need to call setDynamicStatSchemaCollection()`);
            }
        }, 3000);
    }

    async notifyListenersWhenReady() {
        // Awaits data from the required collections, then notifies EventRegistry
        // NOTE: This is called from the dynamic_stats_manager.service, as it needs to run after all
        // dynamic stats have been added to the imsItemTypeRegistry
        await this._dynamicStatSchemaCollection.getAllAsync();
        this._imsItemTypeRegistryReady.notifyListeners();
    }

    /**
     * Using a setter here instead of a ctor arg so this registry can be created
     * higher up the dependency tree.
     *
     * @param {DynamicStatSchemaCollection} dynamicStatSchemaCollection
     */
    setDynamicStatSchemaCollection(dynamicStatSchemaCollection: DynamicStatSchemaCollection) {
        this._dynamicStatSchemaCollection = dynamicStatSchemaCollection;
    }

    async register(type: string, details: ImsItemTypeDetails) {
        this._typeData.set(type, details);
        if (details.helpDetails) {
            const helpDetails: ExtendedHelpDetails = {
                key: type,
                header: details.helpDetails.header || details.displayName,
                tags: details.helpDetails.tags || [],
                hiddenTags: details.helpDetails.hiddenTags || [],
                // Optional
                tourType: TOUR_TYPE__IMS_ITEM_PANEL,
                description: details.helpDetails.description,
                descriptionHtml: details.helpDetails.descriptionHtml,
                quickStartGuideSearchText: details.helpDetails.quickStartGuideSearchText,
                tabbedPanelTour: details.helpDetails.tabbedPanelTour,
                innerTourSteps: details.helpDetails.innerTourSteps,
                onStart: details.helpDetails.onStart,
                onEnd: details.helpDetails.onEnd,
                onClose: details.helpDetails.onClose,
                navigateTo: details.helpDetails.navigateTo,
                beforeStart: details.helpDetails.beforeStart,
            };

            this._helpRegistry.register(helpDetails);
        }
    }

    addOnImsItemTypeRegistryReady(listener) {
        this._imsItemTypeRegistryReady.addEventListener(listener);
    }

    getTypeDetails(type: string | undefined): ImsItemTypeDetails | undefined {
        if (!type) {
            return undefined;
        }
        return this._typeData.has(type) ? Object.assign({}, this._typeData.get(type)) : undefined;
    }

    hasPanelDetails(type: string): boolean {
        return !!this.getPanelDetails(type);
    }

    getPanelDetails(type: string): PanelDetails {
        return this._typeData.has(type) ? this._typeData.get(type).panelDetails : undefined;
    }

    hasHelpDetails(type: string): boolean {
        return !!this.getHelpDetails(type);
    }

    getHelpDetails(type: string): ImsItemHelpDetails {
        return this._typeData.has(type) ? this._typeData.get(type).helpDetails : undefined;
    }

    getRegisteredTypes(): Array<string> {
        return [...this._typeData.keys()];
    }

    getRegisteredStatTypes(): Array<string> {
        const allKeys = this.getRegisteredTypes();

        return allKeys.reduce((accumulator, key) => {
            const type = this._typeData.get(key);
            // Only add keys to return value where isStat is true
            if (type.isStat) {
                accumulator.push(key);
            }
            return accumulator;
        }, []);
    }

    getRegisteredDynamicStatSchemas(): StatDefinition[] {
        const allKeys = this.getRegisteredTypes();
        return allKeys.reduce((accumulator, key) => {
            const type = this._typeData.get(key);

            if (type.isStat && type.statDefinition && type.statDefinition.isDynamicStat) {
                accumulator.push(type.statDefinition);
            }
            return accumulator;
        }, []);
    }
}
